import * as React from 'react'

import { get, useFormContext, type RegisterOptions } from 'react-hook-form'

import { cn } from '@/lib/utils'

import { textareaVariants } from './control-styles'
import { useMergedRef } from './lib/merge-refs'
import { useControlName } from './lib/use-control-name'
import { useLegacyFieldAria } from './lib/use-legacy-field-aria'

/**
 * Área de texto da camada canônica.
 *
 * Não usa a altura de controle: um campo que cresce em linhas não pertence à
 * mesma regra de alinhamento horizontal. Compartilha o resto da linguagem —
 * borda, raio, foco e o piso de 16px no mobile.
 */

type TextareaFieldProps = Omit<
  React.ComponentPropsWithoutRef<'textarea'>,
  'name'
> & {
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  /** Regras do react-hook-form. Prefira validar por schema; isto existe para
   *  paridade com os campos legados durante a migração. */
  rules?: RegisterOptions
}

export const TextareaField = React.forwardRef<
  HTMLTextAreaElement,
  TextareaFieldProps
>(({ className, rows = 4, name: nameProp, rules, onChange, onBlur, ...props }, ref) => {
  const name = useControlName(nameProp)
    const legacyAria = useLegacyFieldAria(name)
  const {
    register,
    formState: { errors }
  } = useFormContext()
  const { ref: registerRef, onChange: registerChange, onBlur: registerBlur, ...field } =
      register(name, rules)

    // `useLegacyFieldAria` só carrega identidade — `id`, `aria-labelledby` e
    // `aria-describedby` —, nunca `aria-invalid`. Montado solto, fora de um
    // `FormGroup` e fora de um `Field`, este campo ficava sem anúncio de erro:
    // a borda vermelha que `textareaVariants` já prevê em
    // `aria-[invalid=true]` nunca era acionada, e o leitor de tela não dizia
    // nada. Dentro do `Field`, o `Slot` deixa a prop do filho vencer — e as
    // duas derivam do mesmo erro, para o mesmo campo, então o resultado é igual.
    const isInvalid = !!get(errors, name)

    // O handler do consumidor é composto com o do register, nunca o substitui:
    // espalhar `props` por cima de `field` faria um `onChange` de fora derrubar
    // o registro do formulário, e o campo pararia de gravar valor em silêncio.
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      void registerChange(event)
      onChange?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      void registerBlur(event)
      onBlur?.(event)
    }

  const mergedRef = useMergedRef(registerRef, ref)

  return (
    <textarea
      rows={rows}
      className={cn(textareaVariants(), className)}
      aria-invalid={isInvalid || undefined}
      {...legacyAria}
      {...field}
      {...props}
      onChange={handleChange}
      onBlur={handleBlur}
      ref={mergedRef}
    />
  )
})
TextareaField.displayName = 'TextareaField'
